import React, { useEffect, useRef, useState } from "react";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import {
  Card,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Pagination,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  AlertTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import JumboSearch from "@jumbo/components/JumboSearch";
import AddBoxIcon from "@mui/icons-material/AddBox";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Div from "@jumbo/shared/Div/Div";
import {
  copyDSOKProduct,
  deleteDSOKProduct,
  exportDSOKProductsCsv,
  getCustomer,
  getDSOKCategories,
  getDSOKProducts,
  previewDSOKProductsImportCsv,
  commitDSOKProductsImportCsv,
  setBackendDomain,
  upsertDSOKProduct,
} from "app/services/api/management";
import CsvImportDialog from "../components/CsvImportDialog";
import { stringToBoolean } from "app/utils/appHelpers";
import { useNavigate } from "react-router-dom";
import DSOKProductItem from "./DSOKProductItem";
import jwtAuthAxiosKiosk from "app/services/auth/jwtAuthKiosk";
import { prepareCategoryLevels } from "app/utils/arrayHelpers";
import { Field } from "formik";
import BindingProductItem from "./BindigProductItem";
import JumboListNoDataPlaceholder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import NoDataPlaceholder from "app/shared/NoDataPlaceholder";

// modalStyle removed; details handled by route

const ITEM_PER_PAGE = 50;

const DSOKProducts = ({ selectMode, onItemUpdate, selectedProducts }) => {
  const { getAuthUser } = useJumboAuth();
  const {
    customer: { url },
  } = getAuthUser();

  const { t, i18n } = useTranslation();
  const { showDialog, hideDialog } = useJumboDialog();
  const [searchName, setSearchName] = useState();
  const [searchBindingName, setSearchBindingName] = useState();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("last");
  const [sortOrder, setSortOrder] = useState("desc");

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [defaultProducts, setDefaultProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyActiveItems, setOnlyActiveItems] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [customer, setCustomer] = useState();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const isMounted = useRef(true);

  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || i18n.language;
  const languages = customer?.dsokSettings?.customer?.languages || [];
  const customerUrl = url || "";
  let currencyCode = "";
  switch (customer?.dsokSettings?.customer?.currencyCode) {
    case "EUR":
      currencyCode = "€";
      break;
    case "USD":
      currencyCode = "$";
      break;
    case "Kč":
      currencyCode = "Kč";
      break;
    default:
      currencyCode = "€";
      break;
  }

  // crosssellActive handled on detail route

  const getProductDisplayName = (product) => {
    const productName = product?.productName;
    const fallbackName = product?.name;
    if (typeof productName === "string") {
      return productName;
    }
    if (
      productName &&
      typeof productName === "object" &&
      !Array.isArray(productName)
    ) {
      const langValue = productName?.[defaultLanguage];
      if (typeof langValue === "string") {
        return langValue;
      }
      const anyString = Object.values(productName).find(
        (v) => typeof v === "string" && v
      );
      if (anyString) return anyString;
    }
    return typeof fallbackName === "string" ? fallbackName : "";
  };

  const normalizeProductId = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "string" || typeof val === "number") return String(val);
    if (typeof val === "object") {
      const id =
        val?._id ??
        val?.id ??
        val?.productId ??
        val?.productKey ??
        val?.key ??
        null;
      return id === null || id === undefined ? null : String(id);
    }
    return null;
  };

  // Categories coming from backend can have `products` in multiple shapes:
  // - Array of ids
  // - Array of product objects
  // - Object map of ids -> true / product
  // - Comma-separated string
  // This helper normalizes to an array of string ids.
  const getCategoryProductIds = (category) => {
    const raw = category?.products;
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw.map(normalizeProductId).filter(Boolean);
    }
    if (typeof raw === "string") {
      return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map(normalizeProductId)
        .filter(Boolean);
    }
    if (typeof raw === "object") {
      return Object.values(raw).map(normalizeProductId).filter(Boolean);
    }
    return [];
  };

  const loadProducts = async () => {
    if (isMounted.current) setLoading(true);
    await getDSOKProducts().then((products) => {
      if (!isMounted.current) return;
      const productArr = Object.keys(products || {}).map((id) => ({
        ...products[id],
      }));

      let filteredProducts =
        searchName || searchBindingName
          ? productArr.filter((product) => {
              // Search matches the product name OR its PLU (additionalInformation.cashRegisterCode).
              const matchesQuery = (query) => {
                const q = String(query || "").toLowerCase();
                const productName = getProductDisplayName(product)
                  .toLowerCase();
                const plu = String(
                  product?.additionalInformation?.cashRegisterCode ?? ""
                ).toLowerCase();
                return productName.includes(q) || plu.includes(q);
              };
              if (searchName) {
                return matchesQuery(searchName);
              }
              if (searchBindingName) {
                return matchesQuery(searchBindingName);
              }
              return false;
            })
          : productArr;
      if (onlyActiveItems) {
        filteredProducts = filteredProducts.filter((item) =>
          selectedProducts ? selectedProducts[item._id]?.status : false
        );
      }
      // Apply sorting
      if (filteredProducts && filteredProducts.length > 0) {
        filteredProducts.sort((a, b) => {
          let aValue, bValue;

          switch (sortBy) {
            case "name":
              aValue =
                a?.productName?.[defaultLanguage] ||
                a?.productName ||
                a?.name ||
                "";
              bValue =
                b?.productName?.[defaultLanguage] ||
                b?.productName ||
                b?.name ||
                "";
              break;
            case "price":
              aValue = parseFloat(a?.price || a?.salePrice || 0);
              bValue = parseFloat(b?.price || b?.salePrice || 0);
              break;
            case "category":
              const aId = String(a?._id ?? "");
              const bId = String(b?._id ?? "");
              const categoriesA = categories?.filter((cat) =>
                getCategoryProductIds(cat).includes(aId)
              );
              const categoriesB = categories?.filter((cat) =>
                getCategoryProductIds(cat).includes(bId)
              );
              aValue =
                categoriesA
                  ?.map(
                    (cat) => cat?.name?.[defaultLanguage] || cat?.name || ""
                  )
                  .join(", ") || "";
              bValue =
                categoriesB
                  ?.map(
                    (cat) => cat?.name?.[defaultLanguage] || cat?.name || ""
                  )
                  .join(", ") || "";
              break;
            case "active":
              aValue = a?.active ? 1 : 0;
              bValue = b?.active ? 1 : 0;
              break;
            case "last":
              aValue = new Date(a?.createdAt || 0);
              bValue = new Date(b?.createdAt || 0);
              break;
            default:
              aValue =
                a?.productName?.[defaultLanguage] ||
                a?.productName ||
                a?.name ||
                "";
              bValue =
                b?.productName?.[defaultLanguage] ||
                b?.productName ||
                b?.name ||
                "";
          }

          if (typeof aValue === "string" && typeof bValue === "string") {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }

          if (sortOrder === "asc") {
            return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
          } else {
            return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
          }
        });
      }

      if (!isMounted.current) return;
      setProducts(filteredProducts || []);
      setDefaultProducts(productArr || []);
    });
    if (isMounted.current) setLoading(false);
  };

  const searchByName = async (searchName, searchBindingName) => {
    if (isMounted.current) setLoading(true);
    setBackendDomain(jwtAuthAxiosKiosk);
    if (!jwtAuthAxiosKiosk.defaults.baseURL) {
      await loadCustomer();
    }
    if (searchName !== undefined || searchBindingName !== undefined) {
      loadProducts();
    }
    if (isMounted.current) setLoading(false);
  };

  useEffect(() => {
    searchByName(searchName, searchBindingName);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchName, onlyActiveItems, searchBindingName, sortBy, sortOrder]);

  const loadCustomer = async () => {
    await getCustomer(customerUrl).then((customer) => {
      if (!isMounted.current) return;
      setCustomer(customer);
    });
  };

  const loadCategories = async () => {
    await getDSOKCategories().then((categories) => {
      if (!isMounted.current) return;
      const categoryArr = Object.keys(categories || {}).map((id) => ({
        ...categories[id],
      }));
      const leveledCategories = prepareCategoryLevels(categoryArr || []);
      setCategories(leveledCategories || []);
    });
    if (isMounted.current) setLoading(false);
  };

  // const loadCategories = async () => {
  //   await getDSOKCategories().then(categories => {
  //     // const { categories } = response || {}
  //     const categoryArr = Object.keys(categories || {}).map(id => ({
  //       ...categories[id]
  //     }))

  //     setCategories(categoryArr || [])
  //   })
  // }

  const loadData = async () => {
    await loadCustomer();
    await loadCategories();
  };

  const searchPageName = selectMode ? "DSOKProductBindings" : "DSOKProducts";

  useEffect(() => {
    isMounted.current = true;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleDelete = async (product) => {
    hideDialog();
    if (isMounted.current) setLoading(true);
    try {
      await deleteDSOKProduct(product._id);
      await loadProducts();
      const message = ` ${t("notifications.wasSuccessful")}`;
      if (isMounted.current) {
        enqueueSnackbar(message, {
          variant: "success",
        });
      }
    } catch (error) {
      const message = `${t("notifications.wasFailed")}`;
      if (isMounted.current) {
        enqueueSnackbar(message, {
          variant: "error",
        });
        setLoading(false);
      }
    }
  };

  // add/edit handled on detail route

  const handleCopyProduct = async (product) => {
    if (isMounted.current) setLoading(true);
    try {
      await copyDSOKProduct(product._id);
      const message = `${t("notifications.wasSuccessful")}`;
      if (isMounted.current) {
        enqueueSnackbar(message, {
          variant: "success",
        });
        setLoading(false);
      }
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      const message = `${t("notifications.wasFailed")}`;
      if (isMounted.current) {
        enqueueSnackbar(message, {
          variant: "error",
        });
        setLoading(false);
      }
    }
  };

  const handleToggleActive = async (product) => {
    if (isMounted.current) setLoading(true);
    try {
      await upsertDSOKProduct({
        ...product,
        isActive: !stringToBoolean(product.isActive),
      });
      await loadProducts();
      if (isMounted.current) {
        enqueueSnackbar(t("notifications.wasSuccessful"), { variant: "success" });
      }
    } catch (error) {
      const message = t("notifications.wasFailed");
      if (isMounted.current) {
        enqueueSnackbar(message, { variant: "error" });
        setLoading(false);
      }
    }
  };

  const handleExportProducts = async () => {
    try {
      const blob = await exportDSOKProductsCsv();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "products.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar(t("notifications.wasFailed"), { variant: "error" });
    }
  };

  const deleteConfirmation = (product) => {
    showDialog({
      variant: "confirm",
      title: t("widgets.confirmDialog.areYouSure"),
      onYes: () => handleDelete(product),
      onNo: () => hideDialog(),
    });
  };
  return (
    <React.Fragment>
      <Typography variant={"h2"} mb={3}>
        {t("pages.dsokProducts.title")}
      </Typography>
      <Stack
        direction="row"
        display="flex"
        alignItems="center"
        justifyContent={"space-between"}
        mb={2}
        spacing={1}
      >
        <Box
          width={selectMode ? "60%" : "40%"}
          display="flex"
          flexDirection="row"
        >
          <Card sx={{ mb: 0, width: "100%", flex: 5 }}>
            <JumboSearch
              page={searchPageName}
              onChange={selectMode ? setSearchBindingName : setSearchName}
              sx={{
                width: "100%",
              }}
              placeholder={t("pages.dsokProducts.searchByNameOrPlu", {
                defaultValue: "Hľadať podľa názvu alebo PLU",
              })}
            />
          </Card>
          {!selectMode && (
            <Tooltip title={t("pages.products.addProduct")}>
              <IconButton
                onClick={() => navigate("/customer/dsok/products/new")}
              >
                <AddBoxIcon />
              </IconButton>
            </Tooltip>
          )}
          {!selectMode && (
            <Tooltip
              title={t("pages.csvImportExport.exportCsv", {
                defaultValue: "Exportovať do CSV",
              })}
            >
              <IconButton onClick={handleExportProducts}>
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          )}
          {!selectMode && (
            <Tooltip
              title={t("pages.csvImportExport.importCsv", {
                defaultValue: "Importovať z CSV",
              })}
            >
              <IconButton onClick={() => setImportDialogOpen(true)}>
                <UploadFileIcon />
              </IconButton>
            </Tooltip>
          )}
          {selectMode && (
            <Div sx={{ mt: 0, mb: 0, ml: 2, flex: 2 }}>
              <FormControlLabel
                control={
                  <Field
                    name={`filter_only_active`}
                    component={Switch}
                    onChange={(e) => setOnlyActiveItems(e.target.checked)}
                  />
                }
                sx={{ width: "100%" }}
                label={t("pages.dsokProducts.showActiveItems")}
                checked={onlyActiveItems || false}
              />
            </Div>
          )}
        </Box>

        {/* Sorting Controls */}
        {!selectMode && (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1, justifyContent: "center" }}
          >
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t("pages.products.sortBy")}</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label={t("pages.products.sortBy")}
              >
                <MenuItem value="last">{t("pages.products.last")}</MenuItem>
                <MenuItem value="name">{t("pages.products.name")}</MenuItem>
                <MenuItem value="price">{t("pages.products.price")}</MenuItem>
                <MenuItem value="category">
                  {t("pages.products.category")}
                </MenuItem>
                <MenuItem value="active">{t("pages.products.active")}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>{t("pages.products.order")}</InputLabel>
              <Select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                label={t("pages.products.order")}
              >
                <MenuItem value="asc">{t("pages.products.ascending")}</MenuItem>
                <MenuItem value="desc">
                  {t("pages.products.descending")}
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}
        <Pagination
          count={Math.ceil(products?.length / ITEM_PER_PAGE)}
          color="primary"
          page={page}
          onChange={(_, val) => setPage(val)}
        />
      </Stack>
      {loading ? (
        <CircularProgress sx={{ m: "5px 50%" }} />
      ) : (
        <>
          {products?.length ? (
            <>
              {selectMode && (
                <Alert severity="info">
                  <AlertTitle>{t("pages.dsokProducts.settingInfo")}</AlertTitle>
                  <p>{t("pages.dsokProducts.isRequiredHelp")}</p>
                  <p>{t("pages.dsokProducts.autoAddHelp")}</p>
                </Alert>
              )}
              {products.map((product, index) => {
                if (
                  index + 1 > ITEM_PER_PAGE * (page - 1) &&
                  index + 1 <= ITEM_PER_PAGE * page
                ) {
                  const productId = String(product?._id ?? "");
                  const productCategories = categories?.filter((category) =>
                    getCategoryProductIds(category).includes(productId)
                  );
                  return selectMode ? (
                    <>
                      <BindingProductItem
                        key={index}
                        item={product}
                        disabled={loading}
                        onDelete={deleteConfirmation}
                        onEdit={() =>
                          navigate(`/customer/dsok/products/${product._id}`)
                        }
                        productLanguages={languages}
                        productCategories={productCategories}
                        defaultLanguage={defaultLanguage}
                        products={defaultProducts}
                        searchName={searchBindingName}
                        selectMode={selectMode}
                        onItemUpdate={onItemUpdate}
                        selectedProducts={selectedProducts}
                      />
                    </>
                  ) : (
                    <DSOKProductItem
                      key={index}
                      item={product}
                      currencyCode={currencyCode}
                      disabled={loading}
                      onDelete={deleteConfirmation}
                      onEdit={() =>
                        navigate(`/customer/dsok/products/${product._id}`)
                      }
                      onCopy={handleCopyProduct}
                      onToggleActive={handleToggleActive}
                      productLanguages={languages}
                      productCategories={productCategories}
                      defaultLanguage={defaultLanguage}
                      products={defaultProducts}
                      searchName={searchName}
                      selectMode={selectMode}
                      onItemUpdate={onItemUpdate}
                      selectedProducts={selectedProducts}
                    />
                  );
                }
                return null;
              })}
            </>
          ) : (
            <JumboListNoDataPlaceholder>
              <NoDataPlaceholder />
            </JumboListNoDataPlaceholder>
          )}
        </>
      )}

      {/* product modal removed; handled by dedicated route */}
      <CsvImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        title={t("pages.csvImportExport.importProductsTitle", {
          defaultValue: "Import produktov (CSV)",
        })}
        previewFn={previewDSOKProductsImportCsv}
        commitFn={commitDSOKProductsImportCsv}
        onImported={loadProducts}
      />
    </React.Fragment>
  );
};

export default DSOKProducts;
